"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Address = {
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  lat: number;
  lng: number;
};

export type OnboardingData = {
  person: {
    name: string;
    mobile: string;
    address: Address;
  };
  platformUsage: string;
  focusAreas: string[];
  currentTools: string[];
  painPoint: string;
};

const ONBOARDING_STORAGE_KEY = "onboarding-data";

export const defaultAddress: Address = {
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  lat: 0,
  lng: 0,
};

const defaultState: OnboardingData = {
  person: {
    name: "",
    mobile: "",
    address: { ...defaultAddress },
  },
  platformUsage: "",
  focusAreas: [],
  currentTools: [],
  painPoint: "",
};

type OnboardingStore = OnboardingData & {
  // getters
  getPerson: () => OnboardingData["person"];

  // actions
  setPerson: (person: Partial<OnboardingData["person"]>) => void;
  setAddress: (
    address: Partial<OnboardingData["person"]["address"]> | null,
  ) => void;
  setPlatformUsage: (value: string) => void;
  setFocusAreas: (areas: string[]) => void;
  setCurrentTools: (tools: string[]) => void;
  setPainPoint: (value: string) => void;
  reset: () => void;
};

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      ...defaultState,

      // ===== GETTER =====
      getPerson: () => get().person,

      // ===== ACTIONS =====
      setPerson: (person) =>
        set((state) => ({
          person: {
            ...state.person,
            ...person,
          },
        })),

      setAddress: (address) =>
        set((state) => ({
          person: {
            ...state.person,
            address: address
              ? {
                  ...state.person.address,
                  ...address,
                }
              : { ...defaultAddress },
          },
        })),

      setPlatformUsage: (value) => set({ platformUsage: value }),

      setFocusAreas: (areas) => set({ focusAreas: areas }),

      setCurrentTools: (tools) => set({ currentTools: tools }),

      setPainPoint: (value) => set({ painPoint: value }),

      reset: () =>
        set(() => ({
          ...defaultState,
        })),
    }),
    {
      name: ONBOARDING_STORAGE_KEY,
    },
  ),
);
